/**
 * CREDITS domain state.
 *
 * ============================================================================
 * CLIENT-SIDE ONLY IN MOCK MODE. In backend mode the balance is read from and
 * written through the server; the frontend still mirrors it locally for UI state.
 * ============================================================================
 *
 * One-time credit model
 * ---------------------
 *   total     every credit added to the account so far
 *   used      what has been spent from that balance
 *   reserved  what in-flight jobs are currently holding aside
 *
 *   available = max(0, total - used - reserved)
 */
import { create } from 'zustand';
import { creditsApi, INITIAL_BALANCE } from '../api/creditsApi';
import { USE_MOCK_API } from '../config';
import {
  availableCredits,
  commitCreditBuckets,
  normalizeCreditBuckets,
  refundCreditBucketSpend,
  releaseCreditBuckets,
  reserveCreditBuckets,
  spendCreditBuckets,
  withCreditTotals,
} from '../lib/credits';
import { newId } from '../lib/ids';
import type { CreditBalance, CreditPack, CreditTransaction } from '../types/domain';
import { IDLE_LOAD, LOADING_LOAD, READY_LOAD, failedLoad, type LoadState } from './loadState';

interface CreditsState extends CreditBalance {
  /** In-memory ledger for this session. The server owns the real history. */
  transactions: CreditTransaction[];

  /**
   * Where the balance READ has got to. See stores/loadState.ts.
   *
   * ==========================================================================
   * THE ONE DOMAIN WHERE A FAILED READ IS NOT A COSMETIC PROBLEM. Every other store's
   * `load.error` means a list is stale. This one means THE APP DOES NOT KNOW WHAT THE
   * BALANCE IS — and `spend()` below is a synchronous local decision made against
   * whatever number is held. So an errored load is the state in which the client is
   * least entitled to act as the authority, which it already is not (§15: "The frontend
   * should not be the ultimate authority for credit balances").
   *
   * The store deliberately does NOT start blocking spends when the read fails. It cannot:
   * the server is not the authority yet either, so refusing to generate because a mock
   * fetch failed would break the app to enforce a rule nothing is enforcing. What it does
   * is hold the fact, so the surface that shows a balance can say it is unconfirmed
   * rather than printing a stale number as though it were current.
   * ==========================================================================
   */
  load: LoadState;

  /**
   * Pack chosen in the buy UI.
   *
   * Store state rather than route-local: the Top-up page and the out-of-credits
   * modal are two separate mounts offering the same choice, and in the prototype
   * they shared one variable. Keeping that shared means picking a pack in the
   * modal and then opening the full page shows the same selection.
   */
  selectedPack: string;
  setSelectedPack: (packId: string) => void;

  /**
   * Spends credits from the account balance.
   *
   * Unlimited accounts spend their monthly allowance first, followed by top-ups.
   *
   * Returns false when the spend did not happen — either because the held balance is short
   * or because the API rejected it — so the caller can show the out-of-credits modal
   * instead of proceeding. Callers must check the return value rather than assuming success.
   */
  spend: (amount: number, reason: string) => Promise<boolean>;
  /** Locks credits for an in-flight apply operation without marking them used yet. */
  reserve: (amount: number) => boolean;
  /** Converts a successful reservation into used credits. */
  commitReservation: (amount: number, reason: string) => void;
  /** Releases credits held by an explicitly failed operation. */
  releaseReservation: (amount: number) => void;
  /** Adds purchased credits to the persistent total. */
  purchasePack: (packId: string) => Promise<CreditPack>;
  /** Grants credits directly to the persistent total. */
  grant: (amount: number, reason: string) => void;
  /** True when the account can afford `amount`. */
  canAfford: (amount: number) => boolean;
  /** Reflects the Unlimited entitlement into the credit model. */
  setUnlimited: (unlimited: boolean) => void;
  /** Fetches the balance through `creditsApi.getBalance()` and replaces what is held. */
  loadBalance: () => Promise<void>;
  /** Fetches the server's transaction history through `creditsApi.getTransactions()`. */
  loadTransactions: () => Promise<void>;
  hydrate: (balance: CreditBalance) => void;
  reset: () => void;
}

function transaction(
  kind: CreditTransaction['kind'],
  amount: number,
  reason: string
): CreditTransaction {
  return { id: newId(), kind, amount, reason, createdAt: new Date().toISOString() };
}

const EMPTY_BALANCE: CreditBalance = {
  total: 0,
  used: 0,
  reserved: 0,
  unlimited: false,
};

const initialBalance: CreditBalance = normalizeCreditBuckets(
  USE_MOCK_API ? INITIAL_BALANCE : EMPTY_BALANCE
);

export const useCreditsStore = create<CreditsState>((set, get) => ({
  ...initialBalance,
  reserved: initialBalance.reserved ?? 0,
  transactions: [],
  load: IDLE_LOAD,
  // Start on the mid-tier pack while leaving the largest pack as the best-value option.
  selectedPack: 'p200',

  setSelectedPack(selectedPack) {
    set({ selectedPack });
  },

  async spend(amount, reason) {
    const s = get();
    const balance = normalizeCreditBuckets(s);
    if (amount <= 0) return true;
    if (availableCredits(balance) < amount) return false;

    const entry = transaction('spend', amount, reason);
    const spent = spendCreditBuckets(balance, amount);
    const monthlySpent = (spent.monthlyUsed ?? 0) - (balance.monthlyUsed ?? 0);
    const topUpSpent = (spent.topUpUsed ?? 0) - (balance.topUpUsed ?? 0);
    set({
      ...spent,
      transactions: [entry, ...s.transactions],
    });

    try {
      const result = await creditsApi.spend(amount, reason);
      if (result.balance) set({ ...normalizeCreditBuckets(result.balance) });
      return true;
    } catch {
      set((current) => ({
        ...refundCreditBucketSpend(current, monthlySpent, topUpSpent),
        transactions: current.transactions.filter((t) => t !== entry),
      }));
      return false;
    }
  },

  reserve(amount) {
    const s = normalizeCreditBuckets(get());
    if (amount <= 0) return true;
    if (availableCredits(s) < amount) return false;
    set({ ...reserveCreditBuckets(s, amount) });
    return true;
  },

  commitReservation(amount, reason) {
    if (amount <= 0) return;
    set((s) => {
      const reserved = Math.min(amount, s.reserved ?? 0);
      if (reserved === 0) return s;
      return {
        ...commitCreditBuckets(s, reserved),
        transactions: [transaction('spend', reserved, reason), ...s.transactions],
      };
    });
  },

  releaseReservation(amount) {
    if (amount <= 0) return;
    set((s) => ({ ...releaseCreditBuckets(s, amount) }));
  },

  async purchasePack(packId) {
    const pack = await creditsApi.purchasePack(packId);
    set((s) => {
      const current = normalizeCreditBuckets(s);
      return {
        ...withCreditTotals({ ...current, topUp: (current.topUp ?? 0) + pack.credits }),
        transactions: [
          transaction('topup', pack.credits, `${pack.credits}-credit pack`),
          ...s.transactions,
        ],
      };
    });
    return pack;
  },

  grant(amount, reason) {
    if (amount <= 0) return;
    set((s) => {
      const current = normalizeCreditBuckets(s);
      return {
        ...withCreditTotals({ ...current, topUp: (current.topUp ?? 0) + amount }),
        transactions: [transaction('grant', amount, reason), ...s.transactions],
      };
    });
  },

  canAfford(amount) {
    const s = normalizeCreditBuckets(get());
    return availableCredits(s) >= amount;
  },

  setUnlimited(unlimited) {
    set({ unlimited });
  },

  async loadBalance() {
    set({ load: LOADING_LOAD });
    try {
      const balance = await creditsApi.getBalance();
      get().hydrate(balance);
      set({ load: READY_LOAD });
    } catch (err) {
      set({ load: failedLoad(err) });
    }
  },

  async loadTransactions() {
    const transactions = await creditsApi.getTransactions();
    set({ transactions });
  },

  hydrate(balance) {
    set({ ...normalizeCreditBuckets(balance) });
  },

  reset() {
    set({ ...initialBalance, reserved: initialBalance.reserved ?? 0, transactions: [], load: IDLE_LOAD });
  },
}));
